import { type ImgHTMLAttributes } from "react";

type Variant = { src: string; width: number };

export type ResponsiveImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "srcSet"> & {
  /** Fallback (largest JPEG) used as <img src> */
  src: string;
  /** AVIF variants, smallest → largest (best compression, served first) */
  avif?: Variant[];
  /** WebP variants, smallest → largest */
  webp: Variant[];
  /** JPEG variants, smallest → largest */
  jpg: Variant[];
  /** sizes attribute, e.g. "(max-width: 768px) 100vw, 50vw" */
  sizes?: string;
  alt: string;
  width: number;
  height: number;
};

function toSrcSet(variants: Variant[]) {
  return variants.map((v) => `${v.src} ${v.width}w`).join(", ");
}

export default function ResponsiveImage({
  src,
  avif,
  webp,
  jpg,
  sizes = "100vw",
  alt,
  width,
  height,
  loading = "lazy",
  decoding = "async",
  className,
  ...rest
}: ResponsiveImageProps) {
  return (
    <picture>
      {avif && avif.length > 0 && (
        <source type="image/avif" srcSet={toSrcSet(avif)} sizes={sizes} />
      )}
      <source type="image/webp" srcSet={toSrcSet(webp)} sizes={sizes} />
      <source type="image/jpeg" srcSet={toSrcSet(jpg)} sizes={sizes} />
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        decoding={decoding}
        className={className}
        {...rest}
      />
    </picture>
  );
}
